/**
 * All / Voluum / Binom filter pills, in the trackers' own brand colours.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Lives beside ProviderBadge on purpose: that file's rule is that once a colour
 * means "Voluum" somewhere it must mean it everywhere, and a filter is just the
 * badge doubling as a control. Any surface mixing the two trackers uses this.
 * A surface that lists Tres Commas too passes `providers` (default: Voluum and
 * Binom, so every existing filter renders exactly as before).
 */

import type { ReactElement } from 'react';

import type { BadgeProvider } from '@/components/ui/ProviderBadge';
import { AllPill } from '@/components/ui/provider-filter/AllPill';
import { TrackerPill } from '@/components/ui/provider-filter/TrackerPill';
import { cn } from '@/lib/utils';

/** The pills a filter offers unless told otherwise. */
const FILTER_PROVIDERS = ['voluum', 'binom'] as const;
type DefaultFilterProvider = (typeof FILTER_PROVIDERS)[number];

export type ProviderFilterValue<P extends BadgeProvider = DefaultFilterProvider> = P | 'all';

export interface ProviderFilterProps<P extends BadgeProvider = DefaultFilterProvider> {
  value: ProviderFilterValue<P>;
  onChange: (value: ProviderFilterValue<P>) => void;
  /** The tracker pills, in order (default: Voluum, Binom). */
  providers?: readonly P[];
  /** Small caps label to the left; omit for a bare pill group. */
  label?: string;
  className?: string;
}

export function ProviderFilter<P extends BadgeProvider = DefaultFilterProvider>({
  value,
  onChange,
  providers = FILTER_PROVIDERS as unknown as readonly P[],
  label,
  className,
}: ProviderFilterProps<P>): ReactElement {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {label ? (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      ) : null}
      <div className="flex items-center gap-1 rounded-md border border-border/60 bg-muted/40 p-1">
        <AllPill on={value === 'all'} onClick={() => onChange('all')} />
        {providers.map((provider) => (
          <TrackerPill
            key={provider}
            provider={provider}
            on={value === provider}
            onClick={() => onChange(provider)}
          />
        ))}
      </div>
    </div>
  );
}

export default ProviderFilter;
